/**
 * Calcula cuántas tareas se generarían para un item de programacionTareas.
 * Replica la lógica del backend (planificadorTareaRepository.programTask).
 */
import {listScheduleCivilDates, toSantiagoCivilDate} from './santiagoSchedule';

export type CalcResult = {
  count: number;
  fechasCount: number;
  cantidadPorEjecucion: number;
  canCalculate: boolean;
  reason?: string; // motivo si no se puede calcular
  uncountableBecauseDirigidoA?: boolean; // true cuando no se puede calcular por dirigidoA !== 'Ejecutor'
};

/**
 * Calcula el número de tareas para un item de programación.
 * @param item - Item de programacionTareas
 * @returns CalcResult o null si no aplica (sin tarea, sin configuración mínima)
 */
export function calcTareasCount(item: any): CalcResult | null {
  if (!item?.tarea?.id) return null;

  // Verificar que tenga destinatarios configurados
  const hasDirigidoA = !!item.dirigidoA;
  const hasDestinatarios =
    (item.dirigidoA === 'Teams' && (item.teams?.length ?? 0) > 0) ||
    (item.dirigidoA === 'Ejecutor' && (item.ejecutores?.length ?? 0) > 0) ||
    (item.dirigidoA === 'Puesto' && (item.puestos?.length ?? 0) > 0);

  if (!hasDirigidoA || !hasDestinatarios) {
    return { count: 0, fechasCount: 0, cantidadPorEjecucion: 0, canCalculate: false, reason: 'Pendiente de configurar' };
  }

  const rawCantidad = parseInt(item.cantidadPorEjecucion, 10);
  const cantidadPorEjecucion = !isNaN(rawCantidad) && rawCantidad > 0 ? rawCantidad : 1;

  // --- Fecha única (sin programar) ---
  if (!item.programar) {
    const fechaBase = item.inicioProgramacion || item.fechaEjecucion;
    if (!fechaBase) {
      return { count: 0, fechasCount: 0, cantidadPorEjecucion, canCalculate: false, reason: 'Indique fecha de ejecución' };
    }
    const result = { count: cantidadPorEjecucion, fechasCount: 1, cantidadPorEjecucion, canCalculate: true };
    if (item.dirigidoA !== 'Ejecutor') {
      return { ...result, canCalculate: false, uncountableBecauseDirigidoA: true };
    }
    return result;
  }

  // --- Programación recurrente ---
  const tipoPeriodicidad = item.tipoPeriodicidad || 'days';
  const periodicidad = Math.max(1, parseInt(item.periodicidad, 10) || 1);

  if (tipoPeriodicidad === 'weeks') {
    const daysOfWeek = item.daysOfWeek;
    if (!daysOfWeek?.length) {
      return {count: 0, fechasCount: 0, cantidadPorEjecucion, canCalculate: false, reason: 'Indique días de la semana'};
    }
  }

  const startYmd = toSantiagoCivilDate(item.inicioProgramacion);
  const endYmd = toSantiagoCivilDate(item.finProgramacion);
  if (!startYmd || !endYmd) {
    return {count: 0, fechasCount: 0, cantidadPorEjecucion, canCalculate: false, reason: 'Revisar fechas'};
  }
  if (startYmd > endYmd) {
    return {
      count: 0,
      fechasCount: 0,
      cantidadPorEjecucion,
      canCalculate: false,
      reason: 'Fecha fin debe ser posterior al inicio',
    };
  }

  const civilDates = listScheduleCivilDates({
    inicio: item.inicioProgramacion,
    fin: item.finProgramacion,
    periodicidad,
    tipoPeriodicidad,
    daysOfWeek: item.daysOfWeek,
  });

  const fechasCount = civilDates.length;

  const count = fechasCount * cantidadPorEjecucion;

  const result = { count, fechasCount, cantidadPorEjecucion, canCalculate: true };
  if (item.dirigidoA !== 'Ejecutor') {
    return { ...result, canCalculate: false, uncountableBecauseDirigidoA: true };
  }
  return result;
}

/**
 * Calcula el total de tareas para todos los items de programacionTareas.
 */
export function calcTotalTareas(items: any[]): { total: number; byItem: (CalcResult | null)[] } {
  if (!items?.length) return { total: 0, byItem: [] };

  const byItem = items.map((item) => calcTareasCount(item));
  const total = byItem.reduce((sum, r) => sum + (r?.canCalculate ? r.count : 0), 0);

  return { total, byItem };
}
